package fr.frux.shared.domain.repository

import fr.frux.core.maps.MapBounds
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.NearbyTrees
import fr.frux.shared.domain.model.ObservableTreeStatus
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreePhoto
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.domain.model.TreeTraitKind
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharedFlow

/**
 * Repository interface for tree-related operations.
 * Provides reactive streams with Flow for real-time updates.
 *
 * Volontairement limité à ce que l'application consomme réellement (#108) : les
 * opérations de contribution restantes (update/delete/verify) existent et sont
 * couvertes par les contract-tests au niveau de `TreeApi` ; elles remonteront ici
 * avec l'écran qui les utilisera, pas avant.
 *
 * Repository d'agrégat, découpage non souhaité à cette taille — cf. #108/#111.
 */
@Suppress("TooManyFunctions")
interface TreeRepository {
    /**
     * Get nearby trees within radius.
     * Emits cached data first, then fetches fresh data from API.
     *
     * @param lat Latitude
     * @param lon Longitude
     * @param radius Radius in kilometers
     * @param filter Species filters evaluated server-side
     * @param bounds Rectangle visible (#621), combiné au rayon côté serveur : les arbres du disque
     * qui tombent hors de l'écran ne consomment plus le lot. `null` garde le disque seul.
     * @return Flow emitting Result<NearbyTrees> — le lot, et le total disponible cote serveur
     * (#155), dont l'appelant deduit si le lot recu est tronque.
     */
    fun getNearbyTrees(
        lat: Double,
        lon: Double,
        radius: Double = 5.0,
        filter: MapFilter = MapFilter.None,
        limit: Int = 20,
        bounds: MapBounds? = null,
    ): Flow<Result<NearbyTrees>>

    /**
     * Rend l'arbre le plus proche sans passage humain daté (#396), ou `null` s'il n'y en a
     * aucun dans le rayon. Volontairement hors de [getNearbyTrees] :
     * - one-shot et sans cache, parce que le cache local SQLDelight ne sait pas évaluer « jamais
     *   vu » (il ignore les observations et les photos) : servir du cache ici désignerait un
     *   arbre où quelqu'un est déjà passé ;
     * - hors de [MapFilter], parce que ce n'est pas un filtre de carte posé par l'utilisateur et
     *   qu'il ne doit apparaître dans aucune puce de filtre.
     */
    suspend fun nearbyNeverSeen(
        lat: Double,
        lon: Double,
        radiusMeters: Double,
    ): Result<Tree?>

    /**
     * Instantané des arbres à proximité, tous statuts d'observation confondus (à la différence de
     * [nearbyNeverSeen]). One-shot sans cache : garde anti-doublon et comptes d'espèces. Triés par
     * distance croissante côté serveur, et jamais écrits dans le cache de la carte, qui n'est pas
     * un filtre posé par l'utilisateur.
     */
    suspend fun nearbyTreesSnapshot(
        lat: Double,
        lon: Double,
        radiusMeters: Double,
        limit: Int,
    ): Result<List<Tree>>

    /**
     * Get tree by ID.
     * Emits cached data first if available, then fetches from API.
     */
    fun getTreeById(id: Long): Flow<Result<Tree>>

    /**
     * Identifiants des arbres dont une observation de terrain vient d'être enregistrée. La carte,
     * qui ne recharge son lot qu'une fois périmé, s'en sert pour rafraîchir le marqueur au retour
     * sans attendre (#648).
     */
    val statusObserved: SharedFlow<Long>

    /**
     * Observe et enregistre le statut courant de l'arbre. Rend l'observation à jour sur le
     * détail (`latestObservation`, `myObservationStatus`) : le prochain [getTreeById] la porte,
     * et [statusObserved] publie l'identifiant.
     */
    suspend fun observeTreeStatus(
        id: Long,
        status: ObservableTreeStatus,
    ): Result<Unit>

    /** Ajoute un trait à l'arbre. Idempotent côté serveur : succès rejoué sans effet. */
    suspend fun addTreeTrait(
        id: Long,
        kind: TreeTraitKind,
    ): Result<Unit>

    /** Retire le trait [kind] posé par l'appelant. */
    suspend fun removeTreeTrait(
        id: Long,
        kind: TreeTraitKind,
    ): Result<Unit>

    /**
     * Crée un arbre. [status] absent est envoyé en `UNKNOWN` explicite (compatibilité avec l'API
     * déjà publiée, ADR 0034). [idempotencyKey] est choisie par l'appelant pour qu'un rejeu après
     * timeout ne crée pas de doublon.
     */
    suspend fun createTree(
        speciesId: Long,
        latitude: Double,
        longitude: Double,
        status: TreeStatus?,
        certainty: Certainty,
        description: String? = null,
        idempotencyKey: String,
    ): Result<Tree>

    /**
     * Ajoute une photo à l'arbre (#223 lot C). [imageBytes] déjà préparés côté client
     * (core-media) ; le serveur re-normalise (variantes, strip EXIF) et borne à 5 MiB.
     * [onProgress] reçoit la fraction envoyée entre 0 et 1, seulement quand le total est connu.
     */
    suspend fun uploadPhoto(
        treeId: Long,
        imageBytes: ByteArray,
        contentType: String = "image/jpeg",
        onProgress: (Float) -> Unit = {},
    ): Result<TreePhoto>

    /** Favoris de l'utilisateur connecté, du plus récent au plus ancien. */
    fun getFavoriteTrees(): Flow<Result<List<Tree>>>

    /** Arbres créés par l'utilisateur connecté, du plus récent au plus ancien. */
    fun getMyTrees(): Flow<Result<List<Tree>>>

    /**
     * Pose ou retire le favori. Les deux sens sont idempotents côté serveur : rejouer le même
     * appel est un succès, pas un conflit.
     */
    suspend fun setFavorite(
        id: Long,
        favorite: Boolean,
    ): Result<Unit>

    /**
     * Signale l'arbre (#129 lots B/C, ADR 0015). `kind` vaut `"MISIDENTIFIED"` (défaut) ou `"GONE"`.
     * Rend l'arbre à jour (`disputed`/`reportedByMe`/`reportedGoneByMe`).
     */
    suspend fun reportTree(
        id: Long,
        suggestedSpeciesId: Long? = null,
        kind: String = "MISIDENTIFIED",
    ): Result<Tree>

    /** Retire le signalement [kind] ouvert de l'utilisateur courant (`MISIDENTIFIED` par défaut). */
    suspend fun withdrawTreeReport(
        id: Long,
        kind: String = "MISIDENTIFIED",
    ): Result<Tree>

    /** Créateur seul : maintient l'identification, résout tous les signalements ouverts. */
    suspend fun resolveTreeReports(id: Long): Result<Tree>

    /**
     * Signale une photo par les pairs (#223 lot D, ADR 0015 réduit) : masquage automatique et
     * immédiat au premier signalement (N=1), photo disparue du détail et du héro.
     */
    suspend fun reportPhoto(photoId: String): Result<Unit>

    /**
     * Supprime une photo, son auteur seul (#247). Le serveur répond 403 pour celle d'autrui :
     * l'UI n'offre l'action que sur ses propres photos, cette borne-ci est la dernière.
     */
    suspend fun deletePhoto(photoId: String): Result<Unit>

    /**
     * Supprime l'arbre (propriétaire seul, #224). Suppression toujours acceptée depuis le
     * cancel-on-delete (#192) : les récoltes encore actives sont annulées et leurs participants
     * notifiés côté serveur, plus de 409 opposé au propriétaire.
     */
    suspend fun deleteTree(id: Long): Result<Unit>
}
